/**
 * 活动页 WebSocket：在页 presence + 活动可用性推送
 * 路径：/signstore/ws/activity-session?token=&activityId=&activityCode=
 */

import { resolveActivityAvailabilityGate } from './act004-activity-gate.js'
import { APP_API_BASE } from '@/config/app.js'

const DEFAULT_PING_MS = 25000
const RECONNECT_MS = 3000

function resolveWsUrl(token, activityId, activityCode) {
  const apiBase = String(APP_API_BASE || '').replace(/\/$/, '')
  let httpBase = apiBase
  if (typeof window !== 'undefined' && apiBase.startsWith('/')) {
    httpBase = window.location.origin + apiBase
  }
  const wsBase = httpBase.replace(/^http/i, 'ws')
  const q = []
  if (token) q.push('token=' + encodeURIComponent(token))
  if (activityId != null && activityId !== '') q.push('activityId=' + encodeURIComponent(String(activityId)))
  if (activityCode) q.push('activityCode=' + encodeURIComponent(String(activityCode)))
  return wsBase + '/signstore/ws/activity-session' + (q.length ? '?' + q.join('&') : '')
}

function readToken() {
  try {
    if (typeof window === 'undefined') return ''
    const raw = window.localStorage.getItem('token')
    if (!raw) return ''
    let t = String(raw).trim()
    if (/^Bearer\s+/i.test(t)) t = t.replace(/^Bearer\s+/i, '').trim()
    return t
  } catch (e) {
    return ''
  }
}

/**
 * @param {Object} options
 * @param {() => ({ activityId?: *, activityCode?: * })} options.getActivity
 * @param {(gate: *) => void} options.onAvailabilityGate
 * @param {() => boolean} [options.shouldRun]
 * @param {number} [options.pingIntervalMs]
 */
export function createActivitySessionWs(options) {
  const getActivity = options && typeof options.getActivity === 'function' ? options.getActivity : () => ({})
  const onGate = options && typeof options.onAvailabilityGate === 'function' ? options.onAvailabilityGate : null
  const shouldRun = options && typeof options.shouldRun === 'function' ? options.shouldRun : () => true
  const pingIntervalMs = (options && options.pingIntervalMs > 0) ? options.pingIntervalMs : DEFAULT_PING_MS

  let socket = null
  let pingTimer = null
  let reconnectTimer = null
  let running = false
  let lastVersion = 0
  let visibilityHandler = null

  function clearPing() {
    if (pingTimer != null) {
      clearInterval(pingTimer)
      pingTimer = null
    }
  }

  function clearReconnect() {
    if (reconnectTimer != null) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
  }

  function applyAvailabilityPayload(data) {
    if (!data || !onGate) return
    const ver = data.version != null ? Number(data.version) : 0
    if (Number.isFinite(ver) && ver > 0 && ver < lastVersion) return
    if (Number.isFinite(ver) && ver > 0) lastVersion = ver
    const gate = resolveActivityAvailabilityGate({
      status: data.status,
      startTime: data.startTime,
      endTime: data.endTime
    })
    onGate(gate)
  }

  function sendPing() {
    if (!socket || socket.readyState !== 1) return
    const act = getActivity() || {}
    try {
      socket.send(JSON.stringify({
        type: 'presence.ping',
        activityId: act.activityId || undefined,
        activityCode: act.activityCode || undefined,
        clientChannel: act.clientChannel || 'H5',
        pageName: act.pageName || undefined,
        clientTs: Date.now()
      }))
    } catch (e) {
      /* ignore */
    }
  }

  function scheduleReconnect() {
    clearReconnect()
    if (!running) return
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      connect()
    }, RECONNECT_MS)
  }

  function connect() {
    if (!running || !shouldRun()) return
    const token = readToken()
    if (!token) {
      scheduleReconnect()
      return
    }
    const act = getActivity() || {}
    if (!act.activityId && !act.activityCode) {
      scheduleReconnect()
      return
    }
    try {
      if (socket) {
        try { socket.close() } catch (e) { /* ignore */ }
        socket = null
      }
      const url = resolveWsUrl(token, act.activityId, act.activityCode)
      socket = new WebSocket(url)
      socket.onopen = () => {
        clearPing()
        sendPing()
        pingTimer = setInterval(sendPing, pingIntervalMs)
      }
      socket.onmessage = (ev) => {
        let data = null
        try {
          data = JSON.parse(ev && ev.data != null ? ev.data : '{}')
        } catch (e) {
          return
        }
        if (!data || !data.type) return
        if (data.type === 'activity.availability') {
          applyAvailabilityPayload(data)
        }
      }
      socket.onclose = () => {
        clearPing()
        socket = null
        if (running) scheduleReconnect()
      }
      socket.onerror = () => {
        try { if (socket) socket.close() } catch (e) { /* ignore */ }
      }
    } catch (e) {
      scheduleReconnect()
    }
  }

  function start() {
    if (running) return
    running = true
    connect()
    if (typeof document !== 'undefined' && !visibilityHandler) {
      visibilityHandler = () => {
        if (!running) return
        if (document.visibilityState === 'hidden') {
          clearPing()
        } else if (document.visibilityState === 'visible') {
          if (!socket || socket.readyState !== 1) {
            connect()
          } else {
            sendPing()
            clearPing()
            pingTimer = setInterval(sendPing, pingIntervalMs)
            try {
              socket.send(JSON.stringify({ type: 'availability.snapshot' }))
            } catch (e) { /* ignore */ }
          }
        }
      }
      document.addEventListener('visibilitychange', visibilityHandler)
    }
  }

  function stop() {
    running = false
    clearPing()
    clearReconnect()
    if (visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', visibilityHandler)
      visibilityHandler = null
    }
    if (socket) {
      try { socket.close() } catch (e) { /* ignore */ }
      socket = null
    }
  }

  return { start, stop, pingNow: sendPing }
}
